"use client";

import { MAX_HEADER_WIDTH } from "@faire/deprecated-retailer-visitor-shared/components/Header/consts";
import {
  HeaderClickItem,
  ClickItem,
} from "@faire/deprecated-retailer-visitor-shared/components/Header/HeaderClickItem";
import { HeaderDropdown } from "@faire/deprecated-retailer-visitor-shared/components/Header/styled";
import { useIsHeaderDropdownVisible } from "@faire/deprecated-retailer-visitor-shared/components/HeaderPriorityRenderer/useIsHeaderDropdownVisible";
import { LazyGrowWithFaireDropdown } from "@faire/deprecated-retailer-visitor-shared/features/GrowWithFaire/LazyGrowWithFaireDropdown";
import { useGrowWithFaireDetails } from "@faire/deprecated-retailer-visitor-shared/features/GrowWithFaire/useGrowWithFaireDetails";
import { useShouldAdvertiseHolidaySpendChallenge } from "@faire/deprecated-retailer-visitor-shared/features/GrowWithFaire/useShouldAdvertiseHolidaySpendChallenge";
import { Twinkle } from "@faire/icons/grow-with-faire/Twinkle";
import { LoadingSkeleton } from "@faire/slate/components/loading-skeleton";
import { Core } from "@faire/slate/foundation/color";
import { trackGrowWithFaireTopNavEntrypointActionCategoryClick } from "@faire/web-api--source/events/growWithFaire/actionCategoryClick/topNavEntrypoint";
import sitewideOfferPath from "@faire/web-api--source/routes/www/sitewide-offer";
import { useViewportWidth } from "@faire/web/common/hooks/useViewportWidth";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { usePathname } from "next/navigation";
import React, { Suspense, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

export const GrowWithFaireEntryPoint = () => {
  const pathname = usePathname();
  const { isEnrolled, isLoading, growWithFaireData } =
    useGrowWithFaireDetails();
  const { isOfferActive, hasEnrolmentPeriodEnded } =
    useShouldAdvertiseHolidaySpendChallenge();
  const viewportWidth = useViewportWidth((width) => width);
  const [shouldShowDropdown, setShouldShowDropdown] = useState(false);
  const { setHeaderDropdownVisibility } = useIsHeaderDropdownVisible();

  const handleEntrypointClick = () => {
    trackGrowWithFaireTopNavEntrypointActionCategoryClick(pathname ?? "");
  };

  const toggleHandler = useCallback(
    (shouldShowDropdown: boolean) => {
      setShouldShowDropdown(shouldShowDropdown);
      setHeaderDropdownVisibility("GROW_WITH_FAIRE", shouldShowDropdown);
    },
    [setHeaderDropdownVisibility]
  );

  if (!isOfferActive) {
    return null;
  }

  if (isLoading) {
    return (
      <EntrypointWrapper>
        <LoadingSkeleton width={100} height={24} />
      </EntrypointWrapper>
    );
  }

  if (hasEnrolmentPeriodEnded && !isEnrolled) {
    return null;
  }

  if (!growWithFaireData?.header_text) {
    return null;
  }

  // place the dropdown at the right edge of the header
  const rightPosition =
    viewportWidth <= MAX_HEADER_WIDTH
      ? 0
      : (viewportWidth - MAX_HEADER_WIDTH) / 2;

  if (!isEnrolled) {
    return (
      <Link
        to={sitewideOfferPath()}
        onClick={handleEntrypointClick}
        data-test-id="grow-with-faire-entry-point-link"
      >
        <GrowWithFaireLabel headerText={growWithFaireData?.header_text} />
      </Link>
    );
  }

  return (
    <GwfHeaderClickItem
      clickChild={
        <AnimatedHeaderDropdown
          $right={`${rightPosition}px`}
          $width="400px"
          $maxHeight="calc(100vh - 110px)"
          className="rounded"
        >
          <Suspense fallback={<div className="h-[200px] w-[400px] p-4" />}>
            <LazyGrowWithFaireDropdown toggleDropdown={toggleHandler} />
          </Suspense>
        </AnimatedHeaderDropdown>
      }
      shouldShowDropdown={shouldShowDropdown}
      toggleDropdown={toggleHandler}
      onClick={handleEntrypointClick}
      dataTestId="grow-with-faire-entry-point"
    >
      <GrowWithFaireLabel headerText={growWithFaireData?.header_text} />
    </GwfHeaderClickItem>
  );
};

const GrowWithFaireLabel: React.FC<{
  headerText: string;
}> = ({ headerText }) => {
  return (
    <EntrypointWrapper>
      <TwinkleIconWrapper>
        <Twinkle fontSize="16px" />
      </TwinkleIconWrapper>
      <Spacer width="1/2x" />
      <Typography variant="paragraphSansMedium">{headerText}</Typography>
    </EntrypointWrapper>
  );
};

const TwinkleIconWrapper = styled.div`
  display: inline-block;
  transition: transform 500ms ease;
  transform-origin: center;
`;

const EntrypointWrapper = styled(Row).attrs({
  align: "center",
  justify: "center",
})`
  border-radius: 24px;
  cursor: pointer;
  padding: ${getSpacing("1x", "2x")};
  &:hover {
    background-color: ${Core.surface.tertiary};
  }
  &:hover ${TwinkleIconWrapper} {
    transform: rotate(90deg);
  }
`;

const GwfHeaderClickItem = styled(HeaderClickItem)`
  ${ClickItem},
  ${ClickItem} * {
    text-decoration: none !important;
  }
`;

const AnimatedHeaderDropdown = styled(HeaderDropdown)`
  transform-origin: top right;
  animation:
    gwf-open 220ms cubic-bezier(0.2, 0.7, 0.2, 1) forwards,
    gwf-fade 120ms ease-out forwards;
  @keyframes gwf-open {
    0% {
      transform: scaleY(0);
    }
    100% {
      transform: scaleY(1);
    }
  }
  @keyframes gwf-fade {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;
